import React, { type ErrorInfo } from 'react';
import { DefaultFallback } from './DefaultFallback';
import { useAwsRum } from '@/src/homepage/common';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<{ error?: Error; resetError?: () => void }>;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    componentName?: string;
    /**
     * Changing this clears a caught error. The Pages Router reuses the boundary
     * across client-side navigations, so without a reset key one caught error
     * would stick for the rest of the session; callers pass the current route.
     */
    resetKey?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    resetKey?: string;
}

/**
 * Implements a custom error boundary component that will catch errors in the app and display a fallback UI
 */
export class ErrorBoundaryComponent extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, resetKey: props.resetKey };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    static getDerivedStateFromProps(
        props: ErrorBoundaryProps,
        state: ErrorBoundaryState,
    ): Partial<ErrorBoundaryState> | null {
        if (props.resetKey !== state.resetKey) {
            return { hasError: false, error: undefined, resetKey: props.resetKey };
        }
        return null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log the error
        console.error(
            `ErrorBoundary caught an error${this.props.componentName ? ` in ${this.props.componentName}` : ''}:`,
            error,
            errorInfo,
        );

        // Call the onError callback if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    resetError = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError) {
            const FallbackComponent = this.props.fallback || DefaultFallback;
            return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
        }

        return this.props.children;
    }
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, fallback, onError, resetKey }) => {
    const awsRum = useAwsRum();

    const handleError = (error: Error, errorInfo: ErrorInfo) => {
        if (awsRum) {
            awsRum.recordError(error);
        }

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const DD_RUM = (window as any).DD_RUM;
            if (DD_RUM) {
                DD_RUM.addError(error, {
                    componentStack: errorInfo.componentStack,
                    errorBoundary: true,
                });
            }
        } catch (ddError) {
            console.error('Failed to report error to DataDog:', ddError);
        }

        if (onError) {
            onError(error, errorInfo);
        }
    };
    return (
        <ErrorBoundaryComponent onError={handleError} fallback={fallback} resetKey={resetKey}>
            {children}
        </ErrorBoundaryComponent>
    );
};
